import React, { useMemo } from "react";
import MaterialIcon from "../../../../shared/components/MaterialIcon";
import { useTranslation } from "../../../../i18n";

function googleStepIndex(step, steps) {
  const index = steps.findIndex((item) => item.key === step);
  return index >= 0 ? index : 0;
}

export function PlatformsConnectionSkeleton() {
  return (
    <div
      className="tonal-card animate-pulse rounded-2xl border border-outline-variant/40 bg-surface-container-lowest py-4 pl-4 pr-11"
      aria-hidden
    >
      <div className="flex min-w-0 items-start gap-4">
        <div className="h-10 w-10 shrink-0 rounded-lg bg-surface-container-high" />
        <div className="min-w-0 flex-1 space-y-3">
          <div className="h-4 max-w-[240px] rounded-md bg-surface-container-high" />
          <div className="h-3 max-w-[160px] rounded-md bg-surface-container-high" />
          <div className="flex flex-wrap gap-2">
            <div className="h-3 w-14 rounded-md bg-surface-container-high" />
            <div className="h-3 w-32 rounded-md bg-surface-container-high" />
            <div className="h-3 w-24 rounded-md bg-surface-container-high" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function PlatformsSidebarSkeleton() {
  return (
    <div className="space-y-6" aria-hidden>
      <div className="animate-pulse overflow-hidden rounded-2xl bg-surface-container-high p-6">
        <div className="h-3 w-28 rounded bg-white/40" />
        <div className="mt-4 h-10 w-20 rounded bg-white/30" />
        <div className="mt-4 space-y-2">
          <div className="h-3 w-full rounded bg-white/25" />
          <div className="h-3 w-4/5 rounded bg-white/25" />
        </div>
        <div className="mt-5 h-2 w-full rounded-full bg-white/20" />
      </div>
      <div className="tonal-card animate-pulse rounded-2xl border border-outline-variant/40 p-5">
        <div className="h-3 w-36 rounded bg-surface-container-high" />
        <div className="mt-4 space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="flex gap-3 border-b border-outline-variant/30 pb-4 last:border-0">
              <div className="h-4 w-10 shrink-0 rounded bg-surface-container-high" />
              <div className="min-w-0 flex-1 space-y-2">
                <div className="h-3.5 w-full max-w-[180px] rounded bg-surface-container-high" />
                <div className="h-3 w-full max-w-[140px] rounded bg-surface-container-high" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function googleStepProgress(step, meta) {
  if (step === "oauth_callback") return 22;
  if (step === "accounts") return 55;
  if (step === "locations" && meta?.total > 0) {
    const slice = 45 / meta.total;
    return Math.min(98, 55 + slice * meta.index);
  }
  if (step === "locations") return 78;
  return 30;
}

export function PlatformsSyncLoadingPanel({
  title,
  detail,
  step,
  meta,
}) {
  const { t } = useTranslation();
  const googleSyncSteps = useMemo(
    () => [
      { key: "oauth_callback", label: t("platformsLoading.stepAuth") },
      { key: "accounts", label: t("platformsLoading.stepAccounts") },
      { key: "locations", label: t("platformsLoading.stepLocations") },
    ],
    [t]
  );

  const showSteps = step && step !== "disconnect";
  const activeStep = googleStepIndex(step, googleSyncSteps);
  const locationProgress =
    step === "locations" && meta?.total > 0
      ? Math.round((meta.index / meta.total) * 100)
      : null;
  const overallProgress = showSteps ? googleStepProgress(step, meta) : 40;

  return (
    <article
      className="w-full rounded-2xl border border-outline-variant/40 bg-surface-container-lowest text-left shadow-soft"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="border-b border-outline-variant/30 bg-gradient-to-r from-surface-container-low to-surface-container-lowest px-5 py-4 sm:px-6 sm:py-5">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary/10 ring-1 ring-secondary/15">
            <MaterialIcon name="sync" className="animate-spin text-[22px] text-secondary" />
          </div>
          <div className="min-w-0">
            <p className="font-headline-md text-[15px] text-primary sm:text-[16px]">{title}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-on-surface-variant">{detail}</p>
          </div>
        </div>
      </div>

      {showSteps ? (
        <div className="px-5 py-4 sm:px-6">
          <ol className="grid grid-cols-3 gap-2 sm:gap-3">
            {googleSyncSteps.map((item, index) => {
              const done = index < activeStep;
              const active = index === activeStep;

              return (
                <li key={item.key} className="min-w-0">
                  <div
                    className={[
                      "flex min-w-0 flex-col items-center gap-1.5 text-center sm:flex-row sm:gap-2 sm:text-left",
                      active ? "text-secondary" : done ? "text-secondary/80" : "text-outline",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-all duration-500",
                        done
                          ? "bg-secondary text-on-secondary scale-100"
                          : active
                            ? "border-2 border-secondary bg-secondary/10 text-secondary scale-110 shadow-[0_0_0_4px_rgba(0,102,102,0.12)]"
                            : "bg-surface-container-high text-on-surface-variant scale-100",
                      ].join(" ")}
                    >
                      {done ? (
                        <MaterialIcon name="check" className="text-[14px]" />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span className="min-w-0 text-[10px] font-medium leading-tight sm:text-[11px]">
                      {item.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>

          {locationProgress != null ? (
            <div className="mt-4">
              <div className="mb-1.5 flex items-center justify-between text-[11px] text-on-surface-variant">
                <span>{t("platformsLoading.loadingLocations")}</span>
                <span className="font-medium tabular-nums text-secondary">{locationProgress}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-high">
                <div
                  className="h-full rounded-full bg-secondary transition-all duration-700 ease-out"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <div className="mb-1.5 flex items-center justify-between text-[11px] text-on-surface-variant">
                <span>{t("platformsLoading.establishingConnection")}</span>
                <span className="font-medium tabular-nums text-secondary">{overallProgress}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-high">
                <div
                  className="h-full rounded-full bg-secondary transition-all duration-700 ease-out"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="px-5 py-4 sm:px-6">
          <div className="h-1.5 overflow-hidden rounded-full bg-surface-container-high">
            <div className="h-full w-2/5 animate-pulse rounded-full bg-secondary/50" />
          </div>
        </div>
      )}
    </article>
  );
}
